import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
let nextOpening = 0;
const principal = binding => JSON.stringify([binding?.revision, binding?.source, binding?.user?.id,
  binding?.user?.tenant_id, binding?.user?.roles || [binding?.user?.role]]);

// Native observations need session ownership, not an order HTTP credential.
export default function useDesktopScope() {
  const { sessionBinding } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const bridge = typeof window === 'undefined' ? null : window.lvbDesktop;
  const identity = principal(sessionBinding);
  const connection = JSON.stringify([bridge?.backendUrl, bridge?.localBackendUrl]);
  const latest = useRef(null);
  const scope = useMemo(() => {
    const roles = sessionBinding?.user?.roles || [sessionBinding?.user?.role];
    const authenticated = !!sessionBinding?.user?.id && !!sessionBinding?.user?.tenant_id
      && sessionBinding.revision === revision;
    const value = {
      bridge, revision, identity, id: ++nextOpening, active: false,
      admin: authenticated && roles.includes('admin'),
      transfer: authenticated && roles.some(role => role === 'admin' || role === 'designer'),
      cloud: authenticated && sessionBinding.source === 'cloud'
        && !(bridge?.localBackendUrl && bridge.backendUrl === bridge.localBackendUrl),
    };
    value.current = () => value.active && latest.current === value
      && revision === getActiveSessionRevision() && principal(sessionBinding) === identity && window.lvbDesktop === bridge
      && JSON.stringify([bridge?.backendUrl, bridge?.localBackendUrl]) === connection;
    return value;
    // identity is the immutable session contract, including roles and source.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision, identity, bridge, connection]);
  latest.current = scope;
  useEffect(() => {
    scope.active = true;
    return () => { scope.active = false; };
  }, [scope]);
  return scope;
}
